/**
 * Asset commands (W1-10a; DESIGN.md §5.5 "Image", §6.13, §6.14; formats-and-packaging P§2.1).
 *
 * Assets are content-addressed (id = SHA-256 of the bytes) and immutable; a document refers to them by id, so a
 * rename only changes the package path. Package paths follow Phira's loader rules (model `assetPathProblem`):
 * '/'-separated, no leading '/', no '.' or '..' segments, never 'export.json' or 'line.png' at the root, never
 * ending in 'click.png', and a hitsound is never exactly 'tap.mp3' / 'flick.mp3' / 'drag.mp3' (Phira plays its
 * built-in sound for those names). New paths are sanitised to ASCII and made unique instead of failing: a reserved
 * name moves into a folder ('sfx/tap.mp3', 'textures/line.png'), 'click.png' becomes 'click_.png'.
 * References: line textures (with the GIF rule), note hitsounds, effect shaders, videos, and the song, illustration
 * and unlock video of the chart. A note hitsound is always a sound file whose path is not a built-in hit sound name,
 * whatever the asset's role (`hitsoundAssetProblem`): replacing and renaming keep that true too.
 */

import { assetPathProblem } from '../model/invariants';
import type { AssetId, AssetRef, AssetRole, ChartMeta } from '../model/types';
import type { MessageRef } from '../util/types';
import { deleteEffectRecord, deleteVideoRecord, isShaderAsset, isVideoAsset } from './effects';
import { dropGifDataUnlessGif, isImageAsset } from './lineType';
import { assetOf, type Patch, type Tx } from './structureKit';
import type { DocCommands } from './types';

const BUILTIN_HITSOUND_NAMES: ReadonlySet<string> = new Set(['tap.mp3', 'flick.mp3', 'drag.mp3']);
const RESERVED_ROOT_NAMES: ReadonlySet<string> = new Set(['export.json', 'line.png']);
const META_ASSET_FIELDS = ['music', 'illustration', 'unlockVideo'] as const;

/** Folder a reserved root name moves into, by role. */
function folderFor(role: AssetRole): string {
  switch (role) {
    case 'hitsound':
      return 'sfx';
    case 'texture':
    case 'gif':
      return 'textures';
    default:
      return 'files';
  }
}

/** One path segment in printable ASCII without characters zip tools or file systems reject. */
function sanitiseSegment(seg: string): string {
  let out = '';
  for (const ch of seg) {
    const c = ch.codePointAt(0) ?? 0;
    out += c < 0x20 || c > 0x7e || ':*?"<>|'.includes(ch) ? '_' : ch;
  }
  return out.trim();
}

/** Splits "a/b.png" into ["a/b", ".png"]. */
function splitExt(path: string): readonly [string, string] {
  const slash = path.lastIndexOf('/');
  const dot = path.lastIndexOf('.');
  return dot > slash + 1 ? [path.slice(0, dot), path.slice(dot)] : [path, ''];
}

/**
 * A valid package path for a new asset of `role` (ASCII, no reserved names; see the module header). Never fails:
 * an unusable name becomes 'file'.
 */
export function sanitiseAssetPath(raw: string, role: AssetRole): string {
  const segs = raw
    .replace(/\\/g, '/')
    .split('/')
    .filter((s) => s !== '' && s !== '.' && s !== '..')
    .map(sanitiseSegment)
    .filter((s) => s !== '' && s !== '.' && s !== '..');
  let path = segs.length > 0 ? segs.join('/') : 'file';
  if (path.endsWith('click.png')) path = `${path.slice(0, -'.png'.length)}_.png`;
  if (RESERVED_ROOT_NAMES.has(path) || (role === 'hitsound' && BUILTIN_HITSOUND_NAMES.has(path))) {
    path = `${folderFor(role)}/${path}`;
  }
  return path;
}

/** `path`, or "name_2.ext", "name_3.ext" ... when taken. */
export function uniqueAssetPath(path: string, taken: ReadonlySet<string>): string {
  if (!taken.has(path)) return path;
  const [stem, ext] = splitExt(path);
  for (let i = 2; ; i++) {
    const candidate = `${stem}_${String(i)}${ext}`;
    if (!taken.has(candidate)) return candidate;
  }
}

function takenPaths(tx: Tx, except?: AssetId): Set<string> {
  const out = new Set<string>();
  for (const a of tx.doc.assets.values()) if (a.id !== except) out.add(a.path);
  return out;
}

/** Number of references to an asset (lines, notes, effects, videos, chart fields). */
export function assetReferenceCount(tx: Tx, id: AssetId): number {
  const u = tx.idx.assetUsers(id);
  return u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length;
}

/**
 * Adds an asset record; the path is sanitised and made unique (noted when it changed). Idempotent per id.
 * `usedAsHitsound`: notes will use it as their hit sound, so the hit sound naming rule applies whatever its role.
 */
export function addAssetRecord(tx: Tx, ref: AssetRef, usedAsHitsound = false): AssetRef {
  const existing = tx.doc.assets.get(ref.id);
  if (existing !== undefined) return existing;
  if (ref.id.length === 0) tx.fail({ key: 'cmd.assets.badId' });
  if (!(Number.isSafeInteger(ref.bytes) && ref.bytes >= 0)) tx.fail({ key: 'cmd.assets.badSize' });
  const path = uniqueAssetPath(sanitiseAssetPath(ref.path, usedAsHitsound ? 'hitsound' : ref.role), takenPaths(tx));
  const rec: AssetRef = path === ref.path ? ref : { ...ref, path };
  tx.insert('assets', rec);
  if (path !== ref.path) tx.note({ key: 'cmd.assets.renamed', params: { from: ref.path, to: path } });
  return rec;
}

const addAsset: DocCommands['addAsset'] = (tx: Tx, { ref }) => addAssetRecord(tx, ref);

/** Points every reference of `from` at `to` (null removes the reference; effects and videos are then deleted). */
export function repointAsset(tx: Tx, from: AssetId, to: AssetId | null): void {
  const u = tx.idx.assetUsers(from);
  const lines = [...u.lines];
  const notes = [...u.notes];
  const effects = [...u.effects];
  const videos = [...u.videos];
  const meta = [...u.meta];
  for (const l of lines) {
    tx.update('lines', l, { texture: to });
    dropGifDataUnlessGif(tx, l);
  }
  for (const n of notes) tx.update('notes', n, { hitsound: to === null ? null : { kind: 'asset', asset: to } });
  for (const e of effects) {
    if (to === null) deleteEffectRecord(tx, e);
    else tx.update('effects', e, { shader: { kind: 'asset', asset: to } });
  }
  for (const v of videos) {
    if (to === null) deleteVideoRecord(tx, v);
    else tx.update('videos', v, { asset: to });
  }
  if (meta.length > 0) {
    const patch: Patch<ChartMeta> = {};
    for (const f of META_ASSET_FIELDS) if (tx.doc.meta[f] === from) patch[f] = to;
    tx.updateSingleton('meta', patch);
  }
}

/** True for sound files (hit sounds, music). */
export function isAudioAsset(a: AssetRef): boolean {
  return a.role === 'hitsound' || a.role === 'music' || a.mime.startsWith('audio/');
}

/** True when Phira plays its built-in sound for a hitsound file at `path` ('tap.mp3', 'drag.mp3', 'flick.mp3'). */
export function isBuiltinHitsoundName(path: string): boolean {
  return BUILTIN_HITSOUND_NAMES.has(path);
}

/**
 * Why `a` cannot be a note's custom hit sound, whatever its role (null: it can): it must be a sound file, and its
 * package path must not be a built-in hit sound name (Phira would silently play its own sound instead of the file).
 */
export function hitsoundAssetProblem(a: AssetRef): MessageRef | null {
  if (!isAudioAsset(a)) return { key: 'cmd.assets.notAudio', params: { path: a.path } };
  if (isBuiltinHitsoundName(a.path)) return { key: 'cmd.assets.builtinHitsoundName', params: { path: a.path } };
  return null;
}

/** Fails when `target` cannot take the place of `from` in one of its uses (an image for textures, ...). */
function checkReplacement(tx: Tx, from: AssetId, target: AssetRef): void {
  const u = tx.idx.assetUsers(from);
  const path = target.path;
  const image = u.lines.length > 0 || u.meta.includes('illustration');
  if (image && !isImageAsset(target)) tx.fail({ key: 'cmd.lines.notImage', params: { path } });
  if (u.meta.includes('music') && !isAudioAsset(target)) tx.fail({ key: 'cmd.assets.notAudio', params: { path } });
  if (u.notes.length > 0) {
    const problem = hitsoundAssetProblem(target);
    if (problem !== null) tx.fail(problem);
  }
  if (u.effects.length > 0 && !isShaderAsset(target)) tx.fail({ key: 'cmd.effects.notShader', params: { path } });
  if ((u.videos.length > 0 || u.meta.includes('unlockVideo')) && !isVideoAsset(target)) {
    tx.fail({ key: 'cmd.videos.notVideo', params: { path } });
  }
}

const replaceAsset: DocCommands['replaceAsset'] = (tx: Tx, { from, to }) => {
  const old = assetOf(tx, from);
  if (from === to) return;
  checkReplacement(tx, from, assetOf(tx, to));
  // Lines whose texture stops being a GIF lose their GIF data in the same step (line rule, noted per line).
  repointAsset(tx, from, to);
  // A placeholder for a missing file has no other purpose: drop it once nothing refers to it.
  if (old.missing === true) tx.remove('assets', from);
};

const removeAsset: DocCommands['removeAsset'] = (tx: Tx, { id, references }) => {
  const a = assetOf(tx, id);
  const count = assetReferenceCount(tx, id);
  if (count > 0) {
    if (references === 'fail') tx.fail({ key: 'cmd.assets.inUse', params: { path: a.path, count } });
    repointAsset(tx, id, null);
    tx.note({ key: 'cmd.assets.referencesRemoved', params: { path: a.path, count } });
  }
  tx.remove('assets', id);
};

const renameAsset: DocCommands['renameAsset'] = (tx: Tx, { id, path }) => {
  const a = assetOf(tx, id);
  const p = path.trim();
  if (p === a.path) return;
  if (/[^\x20-\x7e]/.test(p)) {
    tx.fail({ key: 'cmd.assets.nonAscii' });
  }
  // A file notes use as their hit sound keeps the hit sound naming rule whatever its role.
  const usedAsHitsound = tx.idx.assetUsers(id).notes.length > 0;
  const problem = assetPathProblem(p, usedAsHitsound ? 'hitsound' : a.role);
  if (problem !== null) tx.fail({ key: 'cmd.assets.badPath', params: { path: p, reason: problem } });
  if (takenPaths(tx, id).has(p)) tx.fail({ key: 'cmd.assets.pathTaken', params: { path: p } });
  tx.update('assets', id, { path: p });
};

export const assetCommands = { addAsset, replaceAsset, removeAsset, renameAsset } as const;
